import styled, {
  css,
} from 'styled-components';
import {
  darken,
  desaturate,
} from 'polished';
import {
  NavLink,
} from 'react-router-dom';

export type ButtonAppearances =
  'empty'
  | 'default'
  | 'link'
  | 'danger'
  | 'success'
  | 'primary'
  | 'primaryBright';

export type ButtonProps = {
  /** The base styling to apply to the button. */
  appearance?: ButtonAppearances,
  /** Button's child nodes. */
  children?: React.ReactNode,
  /** Add a classname to the button. */
  className?: string,
  /** Set if the button is disabled. */
  isDisabled?: boolean,
  /** Button icon. */
  icon?: string,
  /** Button icon size. */
  iconSize?: string,
  /** Button icon color. */
  iconColor?: string,
  /** */
  inline?: boolean,
  /** Change the style to indicate the button is selected. */
  isSelected?: boolean,
  /** show spinner. */
  isLoading?: boolean,
  /** fit */
  fit?: boolean,
  /** Handler to be called on click. */
  onClick?: { (): void },
  /** Set the amount of padding in the button. */
  spacing?: 'big' | 'large' | 'compact' | 'default' | 'none',
  /** Set whether it is a button or a form submission. */
  type?: 'button' | 'submit',
  /** Make background transparent and invert text and border colors. */
  ghost?: boolean,
  navLink?: boolean,
  /** Button width. */
  width?: string,
};

export const getButtonStyles = (props: ButtonProps) => {
  const { colors } = props.theme;
  let { width } = props;
  let cursor = 'pointer';
  let padding = '7px 12px';
  let verticalAlign = 'middle';
  let height = 'auto';

  let color = '#505F79';
  let backgroundColor = 'rgba(9, 30, 66, 0.04)';
  let hoverColor = '#505F79';
  let hoverBackgroundColor = 'rgba(9, 30, 66, 0.08)';
  let hoverTextDecoration = 'none';
  let borderRadius = props.borderRadius || '50px';
  let borderColor = '#505F79';
  let fontSize = '12px';
  let display = 'flex';
  let opacity = 'inherit';
  const alignSelf = props.alignSelf || 'align-start';

  if (props.inline) {
    display = 'inline';
  }

  if (props.circled) {
    borderRadius = '50%';
    width = '30px';
    height = '30px';
  }

  switch (props.appearance) {
    case 'empty':
      color = 'black';
      hoverColor = 'black';
      backgroundColor = 'transparent';
      borderColor = 'transparent';
      hoverBackgroundColor = 'transparent';
      break;
    case 'link':
      color = colors.link;
      display = 'inline';
      padding = '0px 4px';
      backgroundColor = 'transparent';
      hoverColor = colors.link;
      borderColor = 'transparent';
      borderRadius = '0';
      hoverBackgroundColor = 'transparent';
      hoverTextDecoration = 'underline';
      break;
    case 'primary':
      color = props.ghost ? colors.primary : '#fff';
      hoverColor = '#fff';
      backgroundColor = props.ghost ? 'transparent' : colors.primary;
      borderColor = props.ghost ? colors.primary : 'transparent';
      hoverBackgroundColor = props.ghost
        ? colors.primary
        : darken(0.05, colors.primary);
      break;
    case 'primaryReverse':
      color = props.ghost ? '#fff' : colors.primary;
      hoverColor = '#fff';
      backgroundColor = props.ghost ? 'transparent' : colors.primary;
      borderColor = props.ghost ? '#fff' : 'transparent';
      hoverBackgroundColor = props.ghost
        ? colors.primary
        : darken(0.05, colors.primary);
      break;
    case 'primaryBright':
      color = props.ghost ? colors.primaryBright : '#fff';
      hoverColor = '#fff';
      backgroundColor = props.ghost ? 'transparent' : colors.primaryBright;
      borderColor = props.ghost ? colors.primaryBright : 'transparent';
      hoverBackgroundColor = props.ghost
        ? colors.primaryBright
        : darken(0.05, colors.primaryBright);
      break;
    case 'warning':
      color = props.ghost ? colors.warning : '#fff';
      hoverColor = '#fff';
      backgroundColor = props.ghost ? 'transparent' : colors.warning;
      borderColor = props.ghost ? colors.warning : 'transparent';
      hoverBackgroundColor = props.ghost
        ? colors.warning
        : darken(0.05, colors.warning);
      break;
    case 'danger':
      color = props.ghost ? colors.danger : '#fff';
      hoverColor = '#fff';
      backgroundColor = props.ghost ? 'transparent' : colors.danger;
      borderColor = props.ghost ? colors.danger : 'transparent';
      hoverBackgroundColor = props.ghost
        ? colors.danger
        : darken(0.05, colors.danger);
      break;
    case 'success':
      color = props.ghost ? colors.success : '#fff';
      hoverColor = '#fff';
      backgroundColor = props.ghost ? 'transparent' : colors.success;
      borderColor = props.ghost ? colors.success : 'transparent';
      hoverBackgroundColor = props.ghost
        ? colors.success
        : darken(0.05, colors.success);
      break;
    default:
      break;
  }

  switch (props.spacing) {
    case 'compact':
      fontSize = '12px';
      padding = '2px 10px';
      break;
    case 'middle':
      fontSize = '12px';
      padding = '6px 12px';
      break;
    case 'large':
      fontSize = '15px';
      padding = '6px 12px';
      break;
    case 'big':
      fontSize = '16px';
      padding = '12px 18px';
      break;
    case 'none':
      padding = '0';
      hoverColor = color;
      verticalAlign = 'baseline';
      borderColor = 'transparent';
      hoverBackgroundColor = backgroundColor;
      break;
    default:
      break;
  }

  if (props.isSelected) {
    color = desaturate(0.6, color);
    hoverColor = color;
    borderColor = desaturate(0.6, borderColor);
    backgroundColor = desaturate(0.6, backgroundColor);
    hoverBackgroundColor = backgroundColor;
  }

  // Disabled
  if (props.isDisabled && !props.isLoading) {
    cursor = 'not-allowed';
    hoverColor = color;
    hoverBackgroundColor = backgroundColor;
  }

  // Fit to parent width
  if (props.fit) {
    width = '100%';
  }

  if (props.fadeIn) {
    opacity = 0;
  }

  return css`
    align-items: baseline;
    align-self: ${alignSelf};
    background-color: ${backgroundColor};
    border-radius: ${borderRadius};
    border-width: 1px;
    border-color: ${borderColor};
    box-sizing: border-box;
    color: ${color};
    cursor: ${cursor};
    display: ${display};
    font-size: ${fontSize};
    font-style: normal;
    margin: 0;
    opacity: ${opacity};
    max-width: 100%;
    outline: none !important;
    padding: ${padding};
    pointer-events: auto;
    text-align: center;
    justify-content: center;
    align-items: center;
    text-decoration: none;
    vertical-align: ${verticalAlign};
    white-space: nowrap;
    width: ${width};
    height: ${height};
    box-shadow: none;
    user-select: none;
    appearance: none;
    border-style: solid;
    text-transform: capitalize;

    &::-moz-focus-inner {
      border: 0;
      margin: 0;
      padding: 0;
    }

    &:hover {
      color: ${hoverColor};
      background-color: ${hoverBackgroundColor};
      text-decoration: ${hoverTextDecoration};
      opacity: 1;
    }

    &:active {
      transition-duration: 0s;
    }

    &:focus {
      outline: none;
      transition-duration: 0s, 0.2s;
    }

    &:focus,
    &:active {
      // filter: grayscale(.15);
    }

    & > *:not(:first-child) {
      margin-left: 0.5rem;
    }
    ${props.noPrint && `
      @media print {
        display: none;
      }
    `}
  `;
};

export const Button = styled.button`
  ${getButtonStyles};
`;

export const Link = styled(NavLink)`
  ${getButtonStyles};
`;
